import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wrench, MagnifyingGlass, Spinner, ArrowsClockwise,
  CheckCircle, XCircle, Warning, Sparkle, Image as ImageIcon, VideoCamera,
  Receipt, Package, Buildings, MapPin, CaretDown, CaretRight, FilePdf, ArrowSquareOut,
  EnvelopeSimple, X,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api, {
  fetchServiceTechDashboard, getServiceVisit, serviceVisitMediaUrl,
  previewServiceTechDigest, sendServiceTechDigest,
} from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

/**
 * ServiceVisitsDashboardPage — staff-only audit view of every Service-Tech
 * visit. Pulls from /api/service-tech/dashboard (single round-trip with
 * server-side filters + pre-computed rollups). Click any row to expand the
 * full AI vision result + media thumbnails.
 */
export default function ServiceVisitsDashboardPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    sn_match: "all",
    has_unmatched: "all",
    q: "",
    limit: 100,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState({}); // { [visit_id]: full visit doc with media }
  const [digestModal, setDigestModal] = useState(null);     // null | "preview" | "loading"
  const [digestData, setDigestData] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [digestSendResult, setDigestSendResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.sn_match !== "all") params.sn_match = filters.sn_match;
      if (filters.has_unmatched !== "all") params.has_unmatched = filters.has_unmatched;
      if (filters.q) params.q = filters.q;
      params.limit = filters.limit;
      const res = await fetchServiceTechDashboard(params);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = useCallback(async (visitId) => {
    if (expandedId === visitId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(visitId);
    if (!expandedDetail[visitId]) {
      try {
        const v = await getServiceVisit(visitId);
        setExpandedDetail((d) => ({ ...d, [visitId]: v }));
      } catch (e) { /* surface inline */
        setExpandedDetail((d) => ({ ...d, [visitId]: { error: e?.response?.data?.detail || "Could not load visit" } }));
      }
    }
  }, [expandedId, expandedDetail]);

  const openDigestPreview = useCallback(async () => {
    setDigestModal("loading");
    setDigestSendResult(null);
    try {
      const d = await previewServiceTechDigest();
      setDigestData(d);
      setDigestModal("preview");
    } catch (e) {
      setDigestData(null);
      setDigestModal("preview");
      setDigestSendResult({ failed: [{ err: e?.response?.data?.detail || e?.message || "Could not load preview" }] });
    }
  }, []);

  const sendDigestNow = useCallback(async (recipients) => {
    if (!recipients || !recipients.length) return;
    setDigestSending(true);
    setDigestSendResult(null);
    try {
      const r = await sendServiceTechDigest({ recipients });
      setDigestSendResult(r);
    } catch (e) {
      setDigestSendResult({ failed: [{ err: e?.response?.data?.detail || e?.message || "Send failed" }] });
    } finally {
      setDigestSending(false);
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="svc-tech-dashboard-page">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={tx("action.back", "Back")} data-testid="dash-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Wrench size={20} weight="fill" className="text-violet-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">{tx("svd.title", "Service-Visit Dashboard")}</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">Admin</span>
          <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100" disabled={loading} data-testid="dash-refresh">
            {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
            {tx("action.refresh", "Refresh")}
          </button>
          <button onClick={openDigestPreview} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-violet-600 text-white border border-violet-700 rounded-sm hover:bg-violet-700" data-testid="dash-digest-btn">
            <EnvelopeSimple size={12} weight="fill" />
            Weekly digest
          </button>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      {digestModal && (
        <DigestModal
          mode={digestModal}
          data={digestData}
          sending={digestSending}
          result={digestSendResult}
          onClose={() => { setDigestModal(null); setDigestSendResult(null); }}
          onSend={sendDigestNow}
          adminEmail={user?.email}
        />
      )}

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        <Metrics metrics={data?.metrics} loading={loading} />

        <Filters filters={filters} setFilters={setFilters} />

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-3 py-2 mb-3" data-testid="dash-error">{String(error)}</div>
        )}

        <VisitsTable
          items={data?.items || []}
          loading={loading}
          expandedId={expandedId}
          expandedDetail={expandedDetail}
          onToggle={toggleExpand}
          onJumpToQuote={(qid) => navigate(`/quote/${encodeURIComponent(qid)}`)}
        />
      </main>
    </div>
  );
}

// ─── Metrics row ───
function Metrics({ metrics, loading }) {
  const tx = useT();
  const m = metrics || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4" data-testid="dash-metrics">
      <Stat label="Total" value={loading ? "—" : m.total ?? 0} tone="slate"
        tip={tx("svd.tip_total", "All service visits in the current filter window — both drafts and completed.")} />
      <Stat label="Completed" value={loading ? "—" : m.completed ?? 0} tone="emerald"
        tip={tx("svd.tip_completed", "Visits the technician has submitted as finished — work order is closed.")} />
      <Stat label="Draft" value={loading ? "—" : m.draft ?? 0} tone="amber"
        tip={tx("svd.tip_draft", "Visits the technician started but hasn't submitted yet. These stay open until closed.")} />
      <Stat label={tx("svd.sn_match", "SN match")} value={loading || m.sn_match_pct == null ? "—" : `${m.sn_match_pct}%`} tone={m.sn_match_pct != null && m.sn_match_pct < 80 ? "rose" : "blue"}
        tip={tx("svd.tip_sn_match", "Percentage of visits where the technician captured a serial number that resolved to a real compressor in the database. Below 80% means training is needed.")} />
      <Stat label="Blocked" value={loading ? "—" : m.blocked_count ?? 0} tone={m.blocked_count > 0 ? "rose" : "slate"}
        tip={tx("svd.tip_blocked", "Visits flagged as blocked — usually missing parts, customer no-show, or access issues. Need follow-up.")} />
      <Stat label="Splits" value={loading ? "—" : m.splits_count ?? 0} tone="violet"
        tip={tx("svd.tip_splits", "Visits where labour/revenue was split across multiple technicians (team jobs).")} />
      <Stat label="Unmatched" value={loading ? "—" : m.unmatched_total ?? 0} tone={m.unmatched_total > 0 ? "amber" : "slate"}
        tip={tx("svd.tip_unmatched", "Visits whose captured serial number didn't match any compressor on file — needs manual reconciliation.")} />
    </div>
  );
}

function Stat({ label, value, tone, tip }) {
  const tones = {
    slate: "border-slate-200 text-slate-800",
    emerald: "border-emerald-200 text-emerald-700",
    amber: "border-amber-200 text-amber-700",
    blue: "border-blue-200 text-blue-700",
    rose: "border-rose-200 text-rose-700",
    violet: "border-violet-200 text-violet-700",
  }[tone] || "border-slate-200 text-slate-800";
  return (
    <div title={tip || undefined}
         className={`bg-white rounded-md border-2 ${tones} px-3 py-2 ${tip ? "cursor-help" : ""}`}>
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="text-xl font-black font-['Chivo']">{value}</div>
    </div>
  );
}

// ─── Filter bar ───
function Filters({ filters, setFilters }) {
  const tx = useT();
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="bg-white rounded-md border border-slate-200 p-2 md:p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="dash-filters">
      <div className="relative flex-1 min-w-[180px]">
        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={filters.q}
          onChange={(e) => set("q")(e.target.value)}
          placeholder={tx("svd.search_placeholder", "Search serial / company / tech / product…")}
          className="pl-7 h-8 text-xs"
          data-testid="dash-search"
        />
      </div>
      <Select label="Status" value={filters.status} onChange={set("status")} options={[
        { v: "all", l: "All" }, { v: "completed", l: "Completed" }, { v: "draft", l: "Draft" },
      ]} testId="dash-status-filter" />
      <Select label={tx("svd.sn_check", "SN check")} value={filters.sn_match} onChange={set("sn_match")} options={[
        { v: "all", l: "All" }, { v: "match", l: "Match" }, { v: "mismatch", l: "Mismatch" }, { v: "unread", l: "Unread" },
      ]} testId="dash-sn-filter" />
      <Select label={tx("svd.unmatched_items", "Unmatched items")} value={filters.has_unmatched} onChange={set("has_unmatched")} options={[
        { v: "all", l: "Any" }, { v: "yes", l: "Has unmatched" }, { v: "no", l: "Clean" },
      ]} testId="dash-unmatched-filter" />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-1 text-[11px]">
      <span className="text-slate-500 uppercase tracking-wider font-bold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-xs px-2 border border-slate-300 rounded-sm bg-white"
        data-testid={testId}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

// ─── Visits table ───
function VisitsTable({ items, loading, expandedId, expandedDetail, onToggle, onJumpToQuote }) {
  const tx = useT();
  if (loading && !items.length) {
    return <div className="bg-white rounded-md border border-slate-200 p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto mb-2" /><div className="text-xs text-slate-500">{tx("svd.loading_visits", "Loading visits…")}</div></div>;
  }
  if (!items.length) {
    return <div className="bg-white rounded-md border border-slate-200 p-12 text-center text-sm text-slate-500" data-testid="dash-empty">{tx("svd.no_visits", "No visits match the filters.")}</div>;
  }
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden" data-testid="dash-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="w-6 px-2 py-2"></th>
            <Th>When</Th>
            <Th>Compressor</Th>
            <Th>Customer</Th>
            <Th>Tech</Th>
            <Th>{tx("svd.sn_check", "SN check")}</Th>
            <Th>Status</Th>
            <Th className="text-right">Applied</Th>
            <Th className="text-right">$ Used</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((v) => (
            <Row
              key={v.id} v={v}
              expanded={expandedId === v.id}
              detail={expandedDetail[v.id]}
              onToggle={() => onToggle(v.id)}
              onJumpToQuote={onJumpToQuote}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-left ${className}`}>{children}</th>;
}

function Row({ v, expanded, detail, onToggle, onJumpToQuote }) {
  const ai = v.ai_result || {};
  const snColor = ai.extracted_sn == null
    ? "bg-slate-100 text-slate-500"
    : ai.sn_match
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700";
  const snLabel = ai.extracted_sn == null
    ? "—"
    : ai.sn_match
      ? `Match ${Math.round((ai.sn_confidence || 0) * 100)}%`
      : "Mismatch";
  const statusColor = {
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    draft: "bg-amber-100 text-amber-700 border-amber-200",
  }[v.status] || "bg-slate-100 text-slate-600 border-slate-200";
  const blocked = v.latest_outcome === "blocked_insufficient";
  return (
    <>
      <tr
        className={`border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer ${expanded ? "bg-blue-50/60" : ""}`}
        onClick={onToggle}
        data-testid={`dash-row-${v.id}`}
      >
        <td className="px-2 py-2 text-slate-300">{expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}</td>
        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{relTime(v.started_at)}</td>
        <td className="px-3 py-2">
          <div className="font-bold text-slate-800">SN {v.serial_number || "—"}</div>
          <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{v.product_name || "—"}</div>
        </td>
        <td className="px-3 py-2">
          <div className="text-slate-700 truncate max-w-[180px]">{v.company_name || v.customer_id || "—"}</div>
        </td>
        <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]">{v.tech_email}</td>
        <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${snColor}`}>{snLabel}</span></td>
        <td className="px-3 py-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${statusColor}`}>{v.status}</span>
          {blocked && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200 px-1 py-0.5 rounded-sm">Blocked</span>}
          {v.unmatched_count > 0 && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 px-1 py-0.5 rounded-sm">{v.unmatched_count} unmatched</span>}
        </td>
        <td className="px-3 py-2 text-right">
          <span className="font-bold text-slate-700">{v.applied_count || 0}</span>
          {v.splits_count > 0 && <span className="ml-1 text-[10px] text-violet-600">({v.splits_count} split)</span>}
        </td>
        <td className="px-3 py-2 text-right font-mono text-slate-700">${(v.alloc_value || 0).toFixed(2)}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50 border-b border-slate-200" data-testid={`dash-row-detail-${v.id}`}>
          <td colSpan={9} className="px-4 py-4">
            <ExpandedDetail v={v} detail={detail} onJumpToQuote={onJumpToQuote} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetail({ v, detail, onJumpToQuote }) {
  const tx = useT();
  if (!detail) {
    return <div className="text-xs text-slate-500"><Spinner size={14} className="animate-spin inline-block mr-1" /> Loading details…</div>;
  }
  if (detail.error) {
    return <div className="text-xs text-rose-700">{String(detail.error)}</div>;
  }
  const ai = detail.ai_result || {};
  const media = detail.media || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {/* Media thumbs */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1.5 flex items-center gap-1.5"><ImageIcon size={11} weight="fill" /> Captures</div>
        <div className="grid grid-cols-2 gap-1.5">
          {media.map((m) => (
            <div key={m.id} className="space-y-0.5">
              <a href={serviceVisitMediaUrl(m.id)} target="_blank" rel="noreferrer" className="block aspect-video rounded-sm border border-slate-200 bg-slate-100 overflow-hidden hover:border-blue-400" title={m.kind}>
                {m.mime_type?.startsWith("video/") ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-900 text-amber-300"><VideoCamera size={20} weight="fill" /></div>
                ) : (
                  <img src={serviceVisitMediaUrl(m.id)} alt={m.kind} className="w-full h-full object-cover" />
                )}
              </a>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold text-center">{m.kind}</div>
            </div>
          ))}
        </div>
      </div>

      {/* AI vision */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1.5 flex items-center gap-1.5"><Sparkle size={11} weight="fill" /> AI Vision</div>
        <div className="bg-white rounded-sm border border-slate-200 p-3 text-xs space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">SN:</span>
            <span className="font-mono font-bold">{ai.expected_sn || "—"}</span>
            <span className="text-slate-400">vs</span>
            <span className={`font-mono font-bold ${ai.sn_match ? "text-emerald-700" : "text-rose-700"}`}>{ai.extracted_sn || "(unread)"}</span>
            {ai.sn_confidence != null && <span className="text-[10px] text-slate-400">{Math.round(ai.sn_confidence * 100)}%</span>}
          </div>
          {ai.items_decoded?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{tx("svd.decoded_items", "Decoded items")} ({ai.items_decoded.length})</div>
              <ul className="text-[11px] space-y-0.5">
                {ai.items_decoded.map((it, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="text-slate-700 truncate">{it.description}</span>
                    <span className="text-slate-500 shrink-0">×{it.qty}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ai.unmatched?.length > 0 && (
            <div className="border-t border-amber-100 pt-1.5">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1 font-bold">Unmatched · {ai.unmatched.length}</div>
              <ul className="text-[11px] space-y-0.5 text-amber-900">
                {ai.unmatched.map((u, i) => <li key={i}>· {u.description} ×{u.qty}{u.reason ? ` — ${u.reason}` : ""}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Allocations */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1.5 flex items-center gap-1.5"><Receipt size={11} weight="fill" /> Allocated</div>
        {ai.applied?.length > 0 ? (
          <div className="bg-white rounded-sm border border-emerald-200 overflow-hidden">
            <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {ai.applied.map((a, i) => (
                <li key={i} className="px-3 py-2 text-[11px]">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-slate-800 truncate flex-1">{a.matched_description || a.detail_id}</span>
                    {a.split && <span className="text-[9px] font-bold uppercase bg-violet-100 text-violet-700 px-1 py-0.5 rounded-sm">Split</span>}
                  </div>
                  <div className="text-slate-500 flex justify-between">
                    <span>used {a.used_qty} {a.split && `· ${a.remaining_qty} left`}</span>
                    {a.unit_price > 0 && <span className="font-mono text-slate-700">${(a.used_qty * a.unit_price).toFixed(2)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-slate-200 p-3 text-[11px] text-slate-400">
            {v.latest_outcome === "blocked_insufficient" ? "Blocked — insufficient parts on quote" : "No allocations applied"}
          </div>
        )}
        {detail.quote_id && (
          <button
            onClick={() => onJumpToQuote(detail.quote_id)}
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-800"
            data-testid={`dash-jump-quote-${v.id}`}
          >
            <FilePdf size={11} weight="fill" /> Open quote {detail.quote_id} <ArrowSquareOut size={10} />
          </button>
        )}
        {(detail.gps?.lat != null) && (
          <div className="mt-2 text-[10px] text-slate-400 flex items-center gap-1"><MapPin size={10} weight="fill" />{detail.gps.lat.toFixed(4)}, {detail.gps.lng.toFixed(4)}</div>
        )}
      </div>
    </div>
  );
}

// ─── Digest preview modal ───
function DigestModal({ mode, data, sending, result, onClose, onSend, adminEmail }) {
  const tx = useT();
  const [recipientsRaw, setRecipientsRaw] = useState(adminEmail || "");
  const recipients = useMemo(
    () => (recipientsRaw || "").split(/[,;\n]/).map((s) => s.trim().toLowerCase()).filter((s) => s && s.includes("@")),
    [recipientsRaw],
  );
  const m = data?.metrics || {};
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="dash-digest-modal">
      <div className="bg-white rounded-t-md md:rounded-md w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <EnvelopeSimple size={18} weight="fill" className="text-violet-700" />
          <h3 className="text-sm font-bold tracking-tight font-['Chivo'] flex-1">{tx("svd.weekly_digest", "Weekly Service-Tech Digest")}</h3>
          {data?.range?.label && <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{data.range.label}</span>}
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-500" data-testid="dash-digest-close"><X size={14} weight="bold" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {mode === "loading" && (
            <div className="text-center py-8"><Spinner size={24} className="animate-spin text-slate-400 mx-auto mb-2" /><div className="text-xs text-slate-500">{tx("svd.building_digest", "Building digest…")}</div></div>
          )}
          {mode === "preview" && data && (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <Stat label="Visits" value={m.total ?? 0} tone="slate"
                  tip={tx("svd.tip_total", "All service visits in the current filter window — both drafts and completed.")} />
                <Stat label="Completed" value={m.completed ?? 0} tone="emerald"
                  tip={tx("svd.tip_completed", "Visits the technician has submitted as finished — work order is closed.")} />
                <Stat label="Draft" value={m.draft ?? 0} tone="amber"
                  tip={tx("svd.tip_draft", "Visits the technician started but hasn't submitted yet. These stay open until closed.")} />
                <Stat label={tx("svd.sn_match", "SN match")} value={m.sn_match_pct == null ? "—" : `${m.sn_match_pct}%`} tone={m.sn_match_pct != null && m.sn_match_pct < 80 ? "rose" : "blue"}
                  tip={tx("svd.tip_sn_match", "Percentage of visits where the technician captured a serial number that resolved to a real compressor in the database. Below 80% means training is needed.")} />
                <Stat label="Blocked" value={m.blocked ?? 0} tone={m.blocked > 0 ? "rose" : "slate"}
                  tip={tx("svd.tip_blocked", "Visits flagged as blocked — usually missing parts, customer no-show, or access issues. Need follow-up.")} />
                <Stat label="Revenue" value={`$${Math.round(m.revenue_allocated || 0).toLocaleString()}`} tone="violet"
                  tip={tx("svd.tip_revenue", "Total revenue allocated to this period across every visit — sum of quote line items linked to the tech's visits.")} />
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{tx("svd.top_techs", "Top techs")}</div>
                <div className="border border-slate-200 rounded-sm overflow-hidden text-xs">
                  {(data.top_techs || []).map((t) => (
                    <div key={t.email} className="flex justify-between px-3 py-1.5 border-b border-slate-100 last:border-b-0">
                      <span className="text-slate-700 truncate">{t.email}</span>
                      <span className="font-bold text-slate-800">{t.count}</span>
                    </div>
                  ))}
                  {!(data.top_techs || []).length && <div className="px-3 py-3 text-center text-slate-400">— none —</div>}
                </div>
              </div>

              {(data.mismatches || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-700 mb-1">⚠ {tx("svd.sn_mismatches", "SN mismatches")} · {data.mismatches.length}</div>
                  <div className="border border-rose-200 rounded-sm overflow-hidden text-xs">
                    {data.mismatches.slice(0, 10).map((r, i) => (
                      <div key={i} className="grid grid-cols-4 gap-2 px-3 py-1.5 border-b border-rose-100 last:border-b-0">
                        <span className="font-mono">{r.expected || "—"}</span>
                        <span className="font-mono text-rose-700">{r.extracted || "—"}</span>
                        <span className="text-slate-700 truncate">{r.company_name}</span>
                        <span className="text-slate-500 truncate">{r.tech_email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(data.blocked || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-1">⚠ {tx("svd.blocked_visits", "Blocked visits")} · {data.blocked.length}</div>
                  <div className="border border-amber-200 rounded-sm overflow-hidden text-xs">
                    {data.blocked.slice(0, 10).map((r, i) => (
                      <div key={i} className="grid grid-cols-4 gap-2 px-3 py-1.5 border-b border-amber-100 last:border-b-0">
                        <span className="font-mono">{r.serial_number || "—"}</span>
                        <span className="text-slate-700 truncate">{r.company_name}</span>
                        <span className="text-slate-500 truncate">{r.tech_email}</span>
                        <span className="text-right text-amber-700 font-bold">{r.shortfall} short</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-200 p-3 bg-slate-50 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <Input
            value={recipientsRaw}
            onChange={(e) => setRecipientsRaw(e.target.value)}
            placeholder={tx("svd.recipients_placeholder", "recipient@usaircompressor.com (comma-separated)")}
            className="text-xs h-8 flex-1"
            data-testid="dash-digest-recipients"
          />
          <button
            onClick={() => onSend(recipients)}
            disabled={sending || !recipients.length}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-violet-600 text-white rounded-sm hover:bg-violet-700 disabled:bg-slate-300 inline-flex items-center justify-center gap-1.5"
            data-testid="dash-digest-send"
          >
            {sending ? <><Spinner size={12} className="animate-spin" /> Sending…</> : <><EnvelopeSimple size={12} weight="fill" /> Send to {recipients.length || "?"}</>}
          </button>
        </div>

        {result && (
          <div className="px-4 py-2 border-t border-slate-200 text-xs">
            {result.sent?.length > 0 && (
              <div className="text-emerald-700 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Sent: {result.sent.join(", ")}</div>
            )}
            {result.skipped?.length > 0 && (
              <div className="text-amber-700">↺ {tx("svd.skipped_already_sent", "Skipped (already sent this week):")} {result.skipped.join(", ")}</div>
            )}
            {result.failed?.length > 0 && (
              <div className="text-rose-700"><Warning size={11} weight="fill" className="inline-block mr-0.5" /> Failed: {result.failed.map(f => typeof f === "string" ? f : f.email || f.err).join(", ")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ───
function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "—"; }
}
